import {
  type Connection,
  PACKET_DATA_SIZE,
  type PublicKey,
  TransactionMessage,
  VersionedTransaction,
} from '@solana/web3.js';
import { estimateComputeUnits } from '../constants/cu-table.js';
import { MORFI_OPINION_PROGRAM_ID } from '../constants/programs.js';
import { getProgram } from '../idl/loader.js';
import type { BuildParams, BuildResult } from '../types/build-result.js';
import { validateOutcomeMetadataText } from '../utils/human-text.js';
import { createBuildResult, invokeMethod, uniqueSignerKeys } from './common.js';
import { deriveCreationPda } from './market-creation-addresses.js';
import {
  buildPrepareOutcomesInstruction,
  type OutcomePreparation,
  type PrepareOutcomesParams,
} from './market-preparation.js';

export interface AddOutcomeParams extends BuildParams {
  creator: PublicKey;
  payer?: PublicKey;
  market: PublicKey;
  marketConfig: PublicKey;
  /** Existing schedule for timed markets; null for conviction markets. */
  eventSchedule: PublicKey | null;
  outcome: OutcomePreparation;
}

/**
 * Atomically activate and prepare the next deterministic outcome. The program
 * validates that `outcomeIndex` is the next active index, so callers never
 * receive a product build that can leave a committed outcome unprepared.
 */
export async function buildAddOutcome(
  connection: Connection,
  params: AddOutcomeParams,
): Promise<BuildResult> {
  validateOutcome(params.outcome);
  validateOutcomeMetadataText(params.outcome);
  const programId = params.programId ?? MORFI_OPINION_PROGRAM_ID;
  const payer = params.payer ?? params.creator;
  const program = await getProgram(connection, programId);
  const add = await invokeMethod(program, 'addOutcome', { ...params.outcome })
    .accounts({
      creator: params.creator,
      market: params.market,
      outcomeMetadata: deriveCreationPda('outcome_metadata', params.market, programId)[0],
      marketConfig: params.marketConfig,
      eventSchedule: params.eventSchedule,
    })
    .instruction();
  const prepareParams: PrepareOutcomesParams = {
    ...params,
    payer,
    requireCreatorSignature: true,
    outcomes: [params.outcome],
  };
  const prepare = await buildPrepareOutcomesInstruction(connection, prepareParams);
  const build = createBuildResult(
    [add, prepare],
    uniqueSignerKeys(params.creator, payer),
    params.computeUnits ?? estimateComputeUnits('add_outcome'),
  );
  if (!fitsPacket(build, payer)) {
    throw new Error('Add-and-prepare outcome exceeds transaction packet size');
  }
  return build;
}

function validateOutcome(outcome: OutcomePreparation): void {
  if (
    !Number.isInteger(outcome.outcomeIndex) ||
    outcome.outcomeIndex < 0 ||
    outcome.outcomeIndex >= 10
  ) {
    throw new Error('Outcome index must be an integer in 0..9');
  }
}

function fitsPacket(build: BuildResult, payer: PublicKey): boolean {
  const message = new TransactionMessage({
    payerKey: payer,
    recentBlockhash: '11111111111111111111111111111111',
    instructions: build.instructions,
  }).compileToV0Message();
  try {
    return new VersionedTransaction(message).serialize().length <= PACKET_DATA_SIZE;
  } catch (error) {
    if (error instanceof RangeError) return false;
    throw error;
  }
}
